import type { FormValues, StorageConfig } from "./types"

const LS_DEFAULT_FORM_STATE_KEY = "use-form-values"

function tryParseJson(inp: string) {
  try {
    const obj = JSON.parse(inp)
    return obj as { [key: string]: any }
  } catch {
    return null
  }
}
function getDefaultFormName() {
  return window.location.pathname
}
function getStorage<T extends FormValues>(storageConfig: StorageConfig<T>) {
  return storageConfig.sessionStorage
    ? window.sessionStorage
    : window.localStorage
}

export function retrieveFormState<T extends FormValues>(
  reference: T,
  storageConfig: StorageConfig<T>,
) {
  const storage = getStorage(storageConfig)

  const lsKey = storageConfig.storeFormStateKey ?? LS_DEFAULT_FORM_STATE_KEY
  const name = storageConfig.storeFormStateName ?? getDefaultFormName()

  const item = storage.getItem(lsKey)
  if (item === null) return null

  const parsed = tryParseJson(item)
  if (parsed === null) return null

  let state: Partial<T> = parsed[name]
  if (typeof state === "undefined") return null

  const retrievedKeys = Object.keys(state)
  for (const key of retrievedKeys)
    if (!Object.hasOwn(reference, key)) return null

  const transforms = storageConfig.transformValues
  if (typeof transforms !== "undefined") {
    const transformedState = Object.entries(state).reduce<Partial<T>>(
      (acc, [key, value]) => {
        if (
          Object.hasOwn(transforms, key) &&
          typeof transforms[key] !== "undefined"
        ) {
          const transformed = transforms[key]?.(value)
          acc[key as keyof T] = transformed ?? reference[key]
        }
        return acc
      },
      reference,
    )

    state = {
      ...state,
      ...transformedState,
    }
  }

  return state
}

export function storeFormState<T extends FormValues>(
  values: T,
  storageConfig: StorageConfig<T>,
) {
  const storage = getStorage(storageConfig)

  const lsKey = storageConfig.storeFormStateKey ?? LS_DEFAULT_FORM_STATE_KEY
  const name = storageConfig.storeFormStateName ?? getDefaultFormName()

  const lsItem = storage.getItem(lsKey)
  const data = lsItem === null ? {} : (tryParseJson(lsItem) ?? {})

  const exclude = storageConfig.excludeFields ?? []
  const doParse = exclude.length > 0
  const parsedValues: Partial<T> = !doParse
    ? values
    : Object.entries(values).reduce(
        (acc, [key, value]) => {
          if (!exclude.includes(key)) acc[key as keyof T] = value
          return acc
        },
        {} as Partial<T>,
      )

  data[name] = parsedValues

  storage.setItem(lsKey, JSON.stringify(data))
}

export function clearFormState<T extends FormValues>(
  storageConfig: StorageConfig<T>,
) {
  const storage = getStorage(storageConfig)

  const lsKey = storageConfig.storeFormStateKey ?? LS_DEFAULT_FORM_STATE_KEY
  const name = storageConfig.storeFormStateName ?? getDefaultFormName()

  const lsItem = storage.getItem(lsKey)
  if (lsItem === null) return

  const data = lsItem === null ? {} : (tryParseJson(lsItem) ?? {})
  if (name in data) delete data[name]

  storage.setItem(lsKey, JSON.stringify(data))
}

export function isKeyofTArray<T extends FormValues>(
  arg: unknown,
): arg is (keyof T)[] {
  return Array.isArray(arg) && arg.every((i) => typeof i === "string")
}
